<template>
  <div
    :class="[
      `design-system-input flex w-full items-center rounded-lg border transition`,
      `outline-2 -outline-offset-1 focus-within:outline`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
  >
    <div
      v-if="symbolPlacement === 'left'"
      class="flex shrink-0 items-center pl-2.5"
    >
      <span class="text-sm font-medium text-muted-foreground">
        {{ currencyInfo?.symbol ?? currency }}
      </span>
    </div>

    <input
      :id="fieldContext?.inputId"
      ref="input"
      v-model="displayValue"
      type="number"
      :class="[
        `block w-full border-none bg-transparent px-2.5 py-1.5 text-sm text-current placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-current`,
      ]"
      :placeholder="placeholder"
      :required="fieldContext?.required.value"
      :disabled="isDisabled"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      v-bind="$attrs"
    />

    <div class="flex shrink-0 items-center gap-1 pr-2.5">
      <span
        v-if="symbolPlacement === 'right'"
        class="text-sm font-medium text-muted-foreground"
      >
        {{ currencyInfo?.symbol ?? currency }}
      </span>
      <span class="text-sm font-medium uppercase text-muted-foreground">
        {{ currency }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, useTemplateRef } from "vue";

import SUPPORTED_CURRENCIES from "@/autogen/supported_currencies.json";

import { injectFieldContext } from "./context";

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<number | undefined>();

const props = defineProps<{
  currency: (typeof SUPPORTED_CURRENCIES)[number]["code"];
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const currencyInfo = computed(() => {
  return SUPPORTED_CURRENCIES.find((c) => c.code === props.currency);
});

const symbolPlacement = computed(() => {
  return currencyInfo.value?.symbol_placement ?? "left";
});

const displayValue = computed({
  get(): number | string {
    return modelValue.value ?? "";
  },
  set(next: number | string) {
    if (next === "" || next === null) {
      modelValue.value = undefined;
    } else {
      modelValue.value = Number(next);
    }
  },
});

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

defineExpose({ input });
</script>
